import type { LogContext, LoggerProvider, LogLevel } from './types'

class Logger {
  private providers: Array<LoggerProvider> = []
  private timers: Map<string, number> = new Map()

  registerProvider(provider: LoggerProvider): void {
    this.providers.push(provider)
  }

  private log(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error,
    category?: string,
  ): void {
    for (const provider of this.providers) {
      provider.log(level, message, context, error, category)
    }
  }

  debug(message: string, context?: LogContext, category?: string): void {
    this.log('debug', message, context, undefined, category)
  }

  info(message: string, context?: LogContext, category?: string): void {
    this.log('info', message, context, undefined, category)
  }

  warning(message: string, context?: LogContext, category?: string): void {
    this.log('warning', message, context, undefined, category)
  }

  error(
    message: string,
    errorOrContext?: Error | LogContext,
    contextOrCategory?: LogContext | string,
    category?: string,
  ): void {
    const error = errorOrContext instanceof Error ? errorOrContext : undefined
    const ctx =
      errorOrContext instanceof Error
        ? typeof contextOrCategory === 'object'
          ? contextOrCategory
          : undefined
        : errorOrContext
    const cat =
      errorOrContext instanceof Error
        ? typeof contextOrCategory === 'string'
          ? contextOrCategory
          : category
        : typeof contextOrCategory === 'string'
          ? contextOrCategory
          : undefined

    this.log('error', message, ctx, error, cat)
  }

  fatal(
    message: string,
    errorOrContext?: Error | LogContext,
    contextOrCategory?: LogContext | string,
    category?: string,
  ): void {
    const error = errorOrContext instanceof Error ? errorOrContext : undefined
    const ctx =
      errorOrContext instanceof Error
        ? typeof contextOrCategory === 'object'
          ? contextOrCategory
          : undefined
        : errorOrContext
    const cat =
      errorOrContext instanceof Error
        ? typeof contextOrCategory === 'string'
          ? contextOrCategory
          : category
        : typeof contextOrCategory === 'string'
          ? contextOrCategory
          : undefined

    this.log('fatal', message, ctx, error, cat)
  }

  startTimer(label: string): void {
    this.timers.set(label, performance.now())
    this.debug(`Timer started: ${label}`, undefined, 'performance')
  }

  endTimer(label: string, context?: LogContext): number {
    const startTime = this.timers.get(label)
    if (!startTime) {
      this.warning(`Timer not found: ${label}`, undefined, 'performance')
      return 0
    }

    const duration = performance.now() - startTime
    this.timers.delete(label)

    this.info(
      `Timer ended: ${label}`,
      {
        ...context,
        duration: `${duration.toFixed(2)}ms`,
      },
      'performance',
    )

    for (const provider of this.providers) {
      provider.addBreadcrumb(`${label} completed`, 'performance', 'info', {
        duration,
        ...context,
      })
    }

    return duration
  }

  setUser(
    userId: string,
    email: string,
    name: string,
    userAccountId: string,
  ): void {
    for (const provider of this.providers) {
      provider.setUser(userId, email, name, userAccountId)
    }
  }

  clearUser(): void {
    for (const provider of this.providers) {
      provider.clearUser()
    }
  }

  setTag(key: string, value: string | number | boolean): void {
    for (const provider of this.providers) {
      provider.setTag(key, value)
    }
  }

  setTags(tags: Record<string, string | number | boolean>): void {
    for (const provider of this.providers) {
      provider.setTags(tags)
    }
  }

  setContext(key: string, context: Record<string, unknown> | null): void {
    for (const provider of this.providers) {
      provider.setContext(key, context)
    }
  }

  startSpan<T>(name: string, op: string, callback: () => T): T {
    for (const provider of this.providers) {
      if (provider.startSpan) {
        return provider.startSpan(name, op, callback)
      }
    }
    return callback()
  }

  addBreadcrumb(
    message: string,
    category?: string,
    level?: string,
    data?: Record<string, unknown>,
  ): void {
    for (const provider of this.providers) {
      provider.addBreadcrumb(message, category, level, data)
    }
  }

  captureEvent(
    message: string,
    level: string = 'info',
    extras?: Record<string, unknown>,
  ): void {
    for (const provider of this.providers) {
      if (provider.captureEvent) {
        provider.captureEvent(message, level, extras)
      }
    }
  }
}

export const logger = new Logger()
